import type { AristaGrafo, NodoGrafo, VarianteGrafo } from "./definicion";
import { VARIANTE_PUBLICACION_AUTOMATICA, VARIANTE_SIN_GENERACION } from "./definicion";

/**
 * El grafo que se DIBUJA, derivado del grafo base + la configuración.
 *
 * `definicion.ts` declara el circuito completo, que es el que corre hoy. El
 * usuario puede apagar dos cosas, y cada una cambia la FORMA del circuito: se
 * funden nodos, se cierra la cadena. Eso no se resuelve en el renderer (que no
 * conoce ningún id) ni en la pantalla (que se llenaría de condicionales), sino
 * acá: una función PURA que recibe datos y devuelve datos
 * (ADR-2026-08-17-mapa-configuracion-y-grafo-derivado).
 *
 * PURA DE VERDAD: sin estado propio, sin `Date`, sin `localStorage`, sin
 * random. Misma entrada ⇒ misma salida, y NUNCA muta lo que recibe: `NODOS` y
 * `ARISTAS` son módulos compartidos con el editor visual y con los tests, y
 * una mutación acá contamina la app entera hasta el próximo reload. Las
 * copias son explícitas y hay un test que lo verifica.
 *
 * Tampoco conoce ningún id: los nodos que entran y a quiénes reemplazan salen
 * de `VarianteGrafo`, que es dato de `definicion.ts`.
 */

export interface ConfigMapa {
  /** El sistema genera las piezas con IA (`true`) o entrega el plan (`false`). */
  generacionDeContenido: boolean;
  /** Publica solo (`true`) o espera la confirmación de una persona (`false`). */
  publicacionAutomatica: boolean;
}

export interface Grafo {
  nodos: NodoGrafo[];
  aristas: AristaGrafo[];
}

/**
 * LOS DEFAULTS SON LO QUE EL PRODUCTO HACE HOY, no "todo prendido".
 *
 *  - `generacionDeContenido: true` — generar con IA ES el producto: el
 *    circuito base tiene el Dir. de IA y la Creación de contenido, y toda la
 *    app existente produce piezas.
 *  - `publicacionAutomatica: false` — hoy NADA se publica solo. El grafo base
 *    trae `vos` ("Confirmación del usuario") como nodo humano del anillo, y no
 *    hay integración que publique.
 *
 * O sea: con los defaults, `grafoSegunConfig` devuelve el grafo BASE tal cual
 * está declarado. Ese es el criterio — el default no puede describir un
 * producto distinto del que se ve al entrar. Hay un test que lo fija.
 */
export const CONFIG_MAPA_POR_DEFECTO: ConfigMapa = {
  generacionDeContenido: true,
  publicacionAutomatica: false,
};

/** Las aristas que forman el CICLO: son las que se puentean al sacar un nodo. */
const esDelCiclo = (tipo: AristaGrafo["tipo"]) => tipo === "flujo" || tipo === "retorno";

/** Identidad de una arista, para no dejar dos iguales tras un reemplazo. */
const claveArista = (a: AristaGrafo) =>
  [a.desde, a.hasta, a.tipo, a.tramo ? `${a.tramo.hacia}@${a.tramo.en}` : ""].join("|");

/**
 * Saca un nodo del CICLO uniendo su anterior con su siguiente.
 *
 * Sólo toca las aristas de ciclo (`flujo`/`retorno`) entre dos nodos del
 * anillo: son las que forman la ronda, y sacar un eslabón de una ronda es
 * empalmar los dos que quedaban a los costados. El resto de las aristas del
 * nodo (lecturas, fuera-de-camino, y las que lo nombran en un
 * `tramo`) NO se puentean: esas se reasignan al nodo nuevo más adelante,
 * porque el vínculo sigue existiendo, sólo que ahora con quien absorbió al
 * nodo.
 *
 * Si al nodo no le entra o no le sale nada del ciclo, no hay empalme posible
 * y se devuelve la lista intacta: la reasignación posterior se hace cargo. Es
 * la salida conservadora — preferir una arista de más (que `validarGrafo` va a
 * ver) antes que borrar un tramo del circuito en silencio.
 *
 * El tipo del empalme: `retorno` si alguna de las dos puntas lo era (el cierre
 * del ciclo tiene que seguir siendo el cierre; el renderer no lo dibuja porque
 * el anillo ya lo traza), y si no, el tipo de la que entraba.
 */
function puentear(
  aristas: AristaGrafo[],
  id: string,
  enAnillo: (id: string) => boolean,
): AristaGrafo[] {
  const puenteable = (a: AristaGrafo) =>
    esDelCiclo(a.tipo) && !a.tramo && enAnillo(a.desde) && enAnillo(a.hasta);
  const entrantes = aristas.filter((a) => a.hasta === id && puenteable(a));
  const salientes = aristas.filter((a) => a.desde === id && puenteable(a));
  if (entrantes.length === 0 || salientes.length === 0) return aristas;

  const resultado: AristaGrafo[] = [];
  for (const a of aristas) {
    // La saliente desaparece dentro del empalme.
    if (a.desde === id && puenteable(a)) continue;
    // La entrante se convierte en el empalme, en su mismo lugar de la lista:
    // el orden de las aristas es el orden de pintado.
    if (a.hasta === id && puenteable(a)) {
      for (const s of salientes) {
        resultado.push({
          desde: a.desde,
          hasta: s.hasta,
          tipo: a.tipo === "retorno" || s.tipo === "retorno" ? "retorno" : a.tipo,
        });
      }
      continue;
    }
    resultado.push(a);
  }
  return resultado;
}

/** Toda mención a un nodo reemplazado pasa a nombrar al nuevo. Devuelve una
 *  arista NUEVA siempre: la de entrada no se toca. */
function renombrar(a: AristaGrafo, reemplazados: Set<string>, idNuevo: string): AristaGrafo {
  const r = (id: string) => (reemplazados.has(id) ? idNuevo : id);
  const nueva: AristaGrafo = { ...a, desde: r(a.desde), hasta: r(a.hasta) };
  if (a.tramo) {
    const hacia = r(a.tramo.hacia);
    // Un tramo cuyos dos extremos quedaron en el MISMO nodo no tiene arco que
    // interpolar: la arista pasa a caer sobre el nodo, que es exactamente lo
    // que daría la geometría (interpolar un ángulo contra sí mismo).
    if (hacia === nueva.hasta) delete nueva.tramo;
    else nueva.tramo = { ...a.tramo, hacia };
  }
  return nueva;
}

/** Después de un reemplazo pueden quedar aristas de un nodo contra sí mismo
 *  (se comió las dos puntas) o dos copias de la misma (dos nodos distintos
 *  que apuntaban al mismo lado). Ninguna de las dos se dibuja bien. */
function limpiar(aristas: AristaGrafo[]): AristaGrafo[] {
  const vistas = new Set<string>();
  return aristas.filter((a) => {
    if (a.desde === a.hasta) return false;
    const k = claveArista(a);
    if (vistas.has(k)) return false;
    vistas.add(k);
    return true;
  });
}

/** Aplica UNA variante sobre un grafo. Si el nodo al que reemplaza no está
 *  (ya se aplicó, o alguien editó el dato), devuelve el grafo sin tocar. */
function aplicarVariante({ nodos, aristas }: Grafo, v: VarianteGrafo): Grafo {
  if (!nodos.some((n) => n.id === v.ocupa)) return { nodos, aristas };

  const absorbidos = new Set(v.absorbe ?? []);
  const porId = new Map(nodos.map((n) => [n.id, n]));
  const enAnillo = (id: string) => porId.get(id)?.posicion.tipo === "anillo";

  // El nodo nuevo entra en el ÍNDICE del que ocupa —el orden del array es el
  // orden de pintado— y los absorbidos salen.
  const nodosNuevos = nodos
    .filter((n) => !absorbidos.has(n.id))
    .map((n) => (n.id === v.ocupa ? v.nodo : n));

  let aristasNuevas = aristas;
  for (const id of absorbidos) aristasNuevas = puentear(aristasNuevas, id, enAnillo);

  const reemplazados = new Set([v.ocupa, ...absorbidos]);
  aristasNuevas = limpiar(aristasNuevas.map((a) => renombrar(a, reemplazados, v.nodo.id)));

  return { nodos: nodosNuevos, aristas: aristasNuevas };
}

/**
 * El grafo a dibujar según la configuración. Devuelve SIEMPRE arrays nuevos,
 * incluso cuando la config es la de por defecto y no hay nada que cambiar: así
 * quien lo consuma no puede llegar a `NODOS`/`ARISTAS` por accidente.
 */
export function grafoSegunConfig(
  nodos: NodoGrafo[],
  aristas: AristaGrafo[],
  config: ConfigMapa,
): Grafo {
  let grafo: Grafo = { nodos: [...nodos], aristas: [...aristas] };
  // Las dos variantes tocan nodos distintos, así que el orden no cambia el
  // resultado; se aplican en el orden en que se leen en el panel.
  if (!config.generacionDeContenido) grafo = aplicarVariante(grafo, VARIANTE_SIN_GENERACION);
  if (config.publicacionAutomatica) {
    grafo = aplicarVariante(grafo, VARIANTE_PUBLICACION_AUTOMATICA);
  }
  return grafo;
}
